/**
 * The glowing elliptical podium the carousel stands on. It is drawn twice:
 * the "back" half sits behind the tiles/props and the "front" half in front
 * of them, which lets props appear to stand inside the ring.
 */

const HOLE_RATIO = 0.66;
const CURB_RISE = 0.16;
const CURB_DROP = CURB_RISE;
const HOLE_FILL = '#031528';

type Layer = 'back' | 'front';

interface PodiumProps {
    rx: number;
    ry: number;
    wall: number;
    pad: number;
    layer: Layer;
    className?: string;
}

function ellipse(cx: number, cy: number, rx: number, ry: number): string {
    return `M ${cx - rx},${cy} a ${rx},${ry} 0 1 0 ${rx * 2},0 a ${rx},${ry} 0 1 0 ${-rx * 2},0`;
}

function frontArc(cx: number, cy: number, rx: number, ry: number): string {
    return `M ${cx - rx},${cy} A ${rx},${ry} 0 0 0 ${cx + rx},${cy}`;
}

function backArc(cx: number, cy: number, rx: number, ry: number): string {
    return `M ${cx - rx},${cy} A ${rx},${ry} 0 0 1 ${cx + rx},${cy}`;
}

function geometry(rx: number, ry: number, wall: number, pad: number, layer: Layer) {
    const width = rx * 2 + pad * 2;
    const height = ry * 2 + wall + pad * 2;
    const cx = pad + rx;
    const cyBase = pad + ry + wall;
    const cyTop = cyBase - wall;
    const holeRx = rx * HOLE_RATIO;
    const holeRy = ry * HOLE_RATIO;
    const curbCy = cyTop - wall * CURB_RISE;
    const innerWallBottom = cyTop + wall * CURB_DROP;
    const upper = layer === 'back' ? `A ${holeRx},${holeRy} 0 0 1 ${cx + holeRx},${curbCy}` : `A ${holeRx},${holeRy} 0 0 0 ${cx + holeRx},${curbCy}`;
    const lower = layer === 'back' ? `A ${holeRx},${holeRy} 0 0 0 ${cx - holeRx},${innerWallBottom}` : `A ${holeRx},${holeRy} 0 0 1 ${cx - holeRx},${innerWallBottom}`;
    const curbCore = Math.max(2.5, rx * 0.0062);
    const innerSeamCore = Math.max(1.8, rx * 0.0042);

    return {
        width,
        height,
        cx,
        cyTop,
        cyBase,
        holeRx,
        holeRy,
        curbCy,
        innerWallBottom,
        deck: `${ellipse(cx, cyTop, rx, ry)} ${ellipse(cx, cyTop, holeRx, holeRy)}`,
        innerWallFace: `M ${cx - holeRx},${curbCy} ${upper} L ${cx + holeRx},${innerWallBottom} ${lower} Z`,
        curbSkirt: `M ${cx - holeRx},${curbCy} A ${holeRx},${holeRy} 0 0 0 ${cx + holeRx},${curbCy} L ${cx + holeRx},${cyTop} A ${holeRx},${holeRy} 0 0 1 ${cx - holeRx},${cyTop} Z`,
        outerWall: `M ${cx - rx},${cyTop} A ${rx},${ry} 0 0 0 ${cx + rx},${cyTop} L ${cx + rx},${cyBase} A ${rx},${ry} 0 0 1 ${cx - rx},${cyBase} Z`,
        splitY: cyTop,
        rimWidth: Math.max(2, rx * 0.0048),
        curbCore,
        curbGlow: curbCore * 3.8,
        innerSeamCore,
        innerSeamBloom: innerSeamCore * 3.2,
    };
}

export function Podium({ rx, ry, wall, pad, layer, className }: PodiumProps) {
    const g = geometry(rx, ry, wall, pad, layer);
    const ids = {
        clip: `wop-podium-clip-${layer}`,
        deck: `wop-podium-deck-${layer}`,
        sheen: `wop-podium-sheen-${layer}`,
        bloom: `wop-podium-bloom-${layer}`,
        wall: `wop-podium-wall-${layer}`,
    };
    const spokeWidth = Math.max(0.65, rx * 0.0012);
    const rimArc = layer === 'back' ? backArc : frontArc;

    return (
        <svg className={className} width={g.width} height={g.height} viewBox={`0 0 ${g.width} ${g.height}`} aria-hidden="true">
            <defs>
                <linearGradient id={ids.deck} x1="0.15" y1="0" x2="0.85" y2="1">
                    <stop offset="0%" stopColor="#b8c8df" />
                    <stop offset="32%" stopColor="#91a5c4" />
                    <stop offset="68%" stopColor="#687fa5" />
                    <stop offset="100%" stopColor="#3d5985" />
                </linearGradient>
                <radialGradient id={ids.sheen} cx="0.5" cy="0" r="0.75" gradientTransform="scale(1 0.5)">
                    <stop offset="0%" stopColor="rgba(244, 250, 255, 0.46)" />
                    <stop offset="38%" stopColor="rgba(187, 215, 244, 0.2)" />
                    <stop offset="100%" stopColor="rgba(150, 210, 255, 0)" />
                </radialGradient>
                <linearGradient id={ids.wall} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1c5798" />
                    <stop offset="38%" stopColor="#0b3973" />
                    <stop offset="100%" stopColor="#031a43" />
                </linearGradient>
                <filter id={ids.bloom} x="-25%" y="-70%" width="150%" height="240%">
                    <feGaussianBlur stdDeviation={Math.max(2.5, rx * 0.006)} result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
                <clipPath id={ids.clip}>
                    {layer === 'back' ? (
                        <rect x="0" y="0" width={g.width} height={g.splitY + 6} />
                    ) : (
                        <rect x="0" y={g.splitY - 6} width={g.width} height={g.height - g.splitY + 6} />
                    )}
                </clipPath>
            </defs>

            <g clipPath={`url(#${ids.clip})`}>
                {layer === 'front' && <path d={g.outerWall} fill={`url(#${ids.wall})`} />}
                {layer === 'front' && <path d={g.deck} fill="#7890b5" fillRule="evenodd" />}
                <path d={g.deck} fill={`url(#${ids.deck})`} fillRule="evenodd" />
                <path d={g.deck} fill={`url(#${ids.sheen})`} fillRule="evenodd" />
                <g opacity="0.42" pointerEvents="none">
                    {Array.from({ length: 32 }, (_, index) => {
                        const angle = (index / 32) * Math.PI * 2;
                        const cos = Math.cos(angle);
                        const sin = Math.sin(angle);

                        return (
                            <line
                                key={index}
                                x1={g.cx + g.holeRx * cos}
                                y1={g.cyTop + g.holeRy * sin}
                                x2={g.cx + rx * cos}
                                y2={g.cyTop + ry * sin}
                                stroke="rgba(20, 48, 91, 0.42)"
                                strokeWidth={spokeWidth}
                            />
                        );
                    })}
                    <ellipse
                        cx={g.cx}
                        cy={g.cyTop}
                        rx={(rx + g.holeRx) / 2}
                        ry={(ry + g.holeRy) / 2}
                        fill="none"
                        stroke="rgba(213, 232, 250, 0.18)"
                        strokeWidth={Math.max(0.6, rx * 0.0011)}
                    />
                </g>
                {layer === 'back' && <path d={g.curbSkirt} fill={HOLE_FILL} />}
                <g filter={`url(#${ids.bloom})`}>
                    <path d={rimArc(g.cx, g.cyTop, rx, ry)} fill="none" stroke="rgba(52, 164, 255, 0.68)" strokeWidth={g.rimWidth * 4.2} strokeLinecap="round" />
                    <path d={rimArc(g.cx, g.cyTop, rx, ry)} fill="none" stroke="#f4fdff" strokeWidth={g.rimWidth} strokeLinecap="round" />
                    {layer === 'front' && (
                        <path d={frontArc(g.cx, g.cyBase, rx, ry)} fill="none" stroke="#ddf8ff" strokeWidth={Math.max(2.5, rx * 0.0065)} />
                    )}
                </g>
            </g>

            {layer === 'back' && (
                <>
                    <path d={g.innerWallFace} fill={HOLE_FILL} />
                    <g filter={`url(#${ids.bloom})`}>
                        <path d={backArc(g.cx, g.curbCy, g.holeRx, g.holeRy)} fill="none" stroke="rgba(64, 174, 255, 0.72)" strokeWidth={g.curbGlow} />
                        <path d={backArc(g.cx, g.curbCy, g.holeRx, g.holeRy)} fill="none" stroke="#f7feff" strokeWidth={g.curbCore} />
                    </g>
                    {[g.cyTop, g.innerWallBottom].map((cy) => (
                        <g key={cy} filter={`url(#${ids.bloom})`}>
                            <path d={backArc(g.cx, cy, g.holeRx, g.holeRy)} fill="none" stroke="rgba(142, 220, 255, 0.55)" strokeWidth={g.innerSeamBloom} strokeLinecap="round" />
                            <path d={backArc(g.cx, cy, g.holeRx, g.holeRy)} fill="none" stroke="#f3fcff" strokeWidth={g.innerSeamCore} strokeLinecap="round" />
                        </g>
                    ))}
                </>
            )}

            {layer === 'front' && (
                <>
                    <path d={g.curbSkirt} fill="#7890b5" />
                    <path d={g.curbSkirt} fill={`url(#${ids.deck})`} />
                    <path d={g.curbSkirt} fill={`url(#${ids.sheen})`} />
                    <g filter={`url(#${ids.bloom})`}>
                        <path d={frontArc(g.cx, g.curbCy, g.holeRx, g.holeRy)} fill="none" stroke="rgba(64, 174, 255, 0.72)" strokeWidth={g.curbGlow} strokeLinecap="round" />
                        <path d={frontArc(g.cx, g.curbCy, g.holeRx, g.holeRy)} fill="none" stroke="#f7feff" strokeWidth={g.curbCore} strokeLinecap="round" />
                    </g>
                </>
            )}
        </svg>
    );
}
